/**
 * QuickNav - Sticky section navigation chip row
 *
 * Horizontal scrollable row of chips for quick navigation to sections.
 * Tracks analytics on chip clicks and supports tier-based visibility.
 *
 * Phase 3.2: QuickNav Component
 * Design: Y2K + Glassmorphism (sticky glassmorphism bar)
 * Accessibility: WCAG AAA compliant
 */

import { ScrollView, StyleSheet, View, Platform, ViewStyle } from 'react-native';
import { createBoxShadow } from '../../../lib/design-tokens';
import { useMemo, useCallback } from 'react';

import { captureEvent } from '../../../lib/analytics';
import { QuickNavChip } from './QuickNavChip';
import type { SectionId } from './useScrollToSection';

/** Section configuration for navigation */
interface SectionConfig {
  id: SectionId;
  labelKey: string;
  defaultLabel: string;
  /** Minimum tier level required (1=Free, 2=Drama+, 3=DramaX) */
  minTier?: number;
}

/** Section configurations with tier requirements */
const SECTION_CONFIGS: SectionConfig[] = [
  { id: 'score', labelKey: 'home.modern.score_nav', defaultLabel: 'Score' },
  { id: 'summary', labelKey: 'home.modern.summary_nav', defaultLabel: 'Summary' },
  { id: 'issues', labelKey: 'home.modern.issues_nav', defaultLabel: 'Issues' },
  { id: 'suggestions', labelKey: 'home.modern.suggestions_nav', defaultLabel: 'Suggestions' },
  { id: 'patterns', labelKey: 'home.modern.patterns_nav', defaultLabel: 'Patterns', minTier: 2 },
  {
    id: 'transformations',
    labelKey: 'home.modern.transformations_nav',
    defaultLabel: 'Insights',
    minTier: 3,
  },
  { id: 'feedback', labelKey: 'home.modern.feedback_nav', defaultLabel: 'Feedback' },
];

interface QuickNavProps {
  /** Available sections that have content to display */
  availableSections: SectionId[];
  /** Currently active/visible section */
  activeSection?: SectionId;
  /** Current tier level (1=Free, 2=Drama+, 3=DramaX) */
  tierLevel: number;
  /** Callback when a section is selected */
  onSectionSelect: (sectionId: SectionId) => void;
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
}

/**
 * QuickNav - Sticky horizontal chip navigation
 *
 * Features:
 * - Horizontal scroll for many sections
 * - Tier-based section visibility
 * - Active section highlighting
 * - Analytics tracking on chip clicks
 * - Glassmorphism sticky styling
 *
 * @example
 * ```tsx
 * <QuickNav
 *   availableSections={['score', 'summary', 'issues']}
 *   activeSection="issues"
 *   tierLevel={2}
 *   onSectionSelect={scrollToSection}
 *   t={t}
 * />
 * ```
 */
export function QuickNav({
  availableSections,
  activeSection,
  tierLevel,
  onSectionSelect,
  t,
  testID = 'quick-nav',
}: QuickNavProps) {
  /**
   * Filter sections based on:
   * 1. Section has content (in availableSections)
   * 2. User tier meets minimum requirement
   */
  const visibleSections = useMemo(() => {
    return SECTION_CONFIGS.filter((config) => {
      // Check if section has content
      if (!availableSections.includes(config.id)) {
        return false;
      }

      // Check tier requirement
      if (config.minTier && tierLevel < config.minTier) {
        return false;
      }

      return true;
    });
  }, [availableSections, tierLevel]);

  /**
   * Handle chip press with analytics tracking
   */
  const handleChipPress = useCallback(
    (sectionId: SectionId) => {
      // Track analytics event
      captureEvent('QuickNav_Chip_Click', {
        section: sectionId,
        tier_level: tierLevel,
        available_sections: availableSections.length,
        platform: Platform.OS,
      });

      // Call the navigation handler
      onSectionSelect(sectionId);
    },
    [tierLevel, availableSections, onSectionSelect]
  );

  // Don't render if no sections to show
  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <View
      style={styles.container}
      role="navigation"
      accessibilityLabel={t('home.modern.quick_nav_label', {
        defaultValue: 'Quick navigation',
      })}
      testID={testID}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        testID={`${testID}-scroll`}
      >
        {visibleSections.map((config) => (
          <QuickNavChip
            key={config.id}
            sectionId={config.id}
            label={t(config.labelKey, { defaultValue: config.defaultLabel })}
            isActive={activeSection === config.id}
            onPress={handleChipPress}
            testID={`${testID}-chip-${config.id}`}
          />
        ))}
      </ScrollView>
    </View>
  );
}

// Platform-aware shadow for QuickNav container
const containerShadow = createBoxShadow({
  color: '#000',
  offsetX: 0,
  offsetY: 2,
  blurRadius: 8,
  opacity: 0.05,
  elevation: 2,
});

/**
 * StyleSheet for QuickNav
 *
 * Design: Sticky glassmorphism bar with Y2K accent
 * - Semi-transparent white background
 * - Lavender border accent
 * - Subtle blur effect (web only)
 * - Drop shadow for depth
 */
const styles = StyleSheet.create({
  container: {
    // Sticky positioning handled by parent ScrollView or FlatList
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(212, 165, 255, 0.25)',
    paddingVertical: 10,
    marginBottom: 12,
    alignSelf: 'center',
    width: '100%',
    maxWidth: 400,
    borderRadius: 12,
    ...containerShadow,
  } as ViewStyle,

  scrollContent: {
    paddingHorizontal: 12,
  },
});

export { SECTION_CONFIGS };
